/**
 * Centralized registry for context menu actions with middleware and error handling support
 */
class ContextMenuActionRegistry {
  constructor() {
    this.actions = new Map();
    this.middleware = [];
    this.errorHandlers = [];
  }

  /**
   * Register an action handler with optional metadata
   * @param {string} actionName - Unique identifier for the action
   * @param {Function} handler - Function that executes the action
   * @param {Object} options - Optional metadata (description, category, etc.)
   * @returns {ContextMenuActionRegistry} This instance for method chaining
   */
  registerAction(actionName, handler, options = {}) {
    if (typeof handler !== "function") {
      throw new Error(`Action handler for '${actionName}' must be a function`);
    }

    this.actions.set(actionName, {
      handler,
      description: options.description || "",
      category: options.category || "general",
      requiresContext: options.requiresContext || false,
    });

    return this;
  }

  /**
   * Register multiple actions from an object map
   * @param {Object} actionMap - Object with action names as keys and handlers as values
   * @param {Object} options - Options applied to all actions in the map
   * @returns {ContextMenuActionRegistry} This instance for method chaining
   */
  registerActions(actionMap, options = {}) {
    Object.entries(actionMap).forEach(([actionName, handler]) => {
      this.registerAction(actionName, handler, options);
    });
    return this;
  }

  /**
   * Add middleware function that runs before action execution
   */
  addMiddleware(middlewareFn) {
    if (typeof middlewareFn !== "function") {
      throw new Error("Middleware must be a function");
    }
    this.middleware.push(middlewareFn);
    return this;
  }

  /**
   * Add error handler
   */
  addErrorHandler(errorHandlerFn) {
    if (typeof errorHandlerFn !== "function") {
      throw new Error("Error handler must be a function");
    }
    this.errorHandlers.push(errorHandlerFn);
    return this;
  }

  /**
   * Execute a registered action with context, middleware processing, and error handling
   * @param {string} actionName - Name of the action to execute
   * @param {Object} context - Context object passed to middleware and action handler
   * @returns {Promise<*>} The result of the action handler
   * @throws {Error} If action is not registered or execution fails
   */
  async executeAction(actionName, context = {}) {
    const action = this.actions.get(actionName);

    if (!action) {
      const error = new Error(`Action '${actionName}' not found in registry`);
      return this.handleError(error, actionName, context);
    }

    try {
      // Run middleware
      let processedContext = context;
      for (const middleware of this.middleware) {
        processedContext = (await middleware(actionName, processedContext)) || processedContext;
      }

      // Validate required context
      if (action.requiresContext && !processedContext) {
        throw new Error(`Action '${actionName}' requires context but none was provided`);
      }

      // Execute the action
      const result = await action.handler(processedContext);

      return result;
    } catch (error) {
      return this.handleError(error, actionName, context);
    }
  }

  /**
   * Handle errors during action execution
   */
  handleError(error, actionName, context) {
    console.error(`Error executing action '${actionName}':`, error);

    // Run error handlers
    this.errorHandlers.forEach((handler) => {
      try {
        handler(error, actionName, context);
      } catch (handlerError) {
        console.error("Error in error handler:", handlerError);
      }
    });

    // Don't throw - let the UI continue to function
    return { error: true, message: error.message };
  }

  /**
   * Check if an action is registered
   */
  hasAction(actionName) {
    return this.actions.has(actionName);
  }

  /**
   * Get all registered actions
   */
  getRegisteredActions() {
    return Array.from(this.actions.keys());
  }

  /**
   * Get action information
   */
  getActionInfo(actionName) {
    return this.actions.get(actionName);
  }

  /**
   * Unregister an action
   */
  unregisterAction(actionName) {
    return this.actions.delete(actionName);
  }

  /**
   * Clear all actions (useful for testing)
   */
  clear() {
    this.actions.clear();
    this.middleware = [];
    this.errorHandlers = [];
  }

  /**
   * Create a scoped executor that automatically includes common context
   */
  createScopedExecutor(commonContext = {}) {
    return (actionName, additionalContext = {}) => {
      return this.executeAction(actionName, {
        ...commonContext,
        ...additionalContext,
      });
    };
  }
}

// Create and export a singleton instance
const contextMenuActionRegistry = new ContextMenuActionRegistry();

// Add default error handler
contextMenuActionRegistry.addErrorHandler((error, actionName) => {
  console.warn(`Context menu action '${actionName}' failed:`, error.message);
});

export default contextMenuActionRegistry;
export { ContextMenuActionRegistry };
